import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import { Textarea } from '@/app/components/ui/textarea';
import { Label } from '@/app/components/ui/label';
import {
  useIncidentReport,
  useInvestigateIncident,
  useResolveIncident,
  useCloseIncident,
} from '@/hooks/use-safety';
import { formatDateOnly } from '@/lib/date-utils';
import type { IncidentStatus, IncidentSeverity } from '@/lib/types';
import { usePermissions } from '@/hooks/use-permissions';

const statusColors: Record<IncidentStatus, string> = {
  reported: 'bg-yellow-100 text-yellow-800',
  under_investigation: 'bg-blue-100 text-blue-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-600',
};

const statusLabels: Record<IncidentStatus, string> = {
  reported: 'Reported',
  under_investigation: 'Under Investigation',
  resolved: 'Resolved',
  closed: 'Closed',
};

const severityColors: Record<IncidentSeverity, string> = {
  minor: 'bg-green-100 text-green-800',
  moderate: 'bg-yellow-100 text-yellow-800',
  serious: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

export function IncidentDetailPage() {
  const { hasMinModuleRole } = usePermissions();
  const canManageSafety = hasMinModuleRole('safety', 'manager');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const incidentId = parseInt(id || '0', 10);
  const { data: incident, isLoading } = useIncidentReport(incidentId);
  const investigateMutation = useInvestigateIncident();
  const resolveMutation = useResolveIncident();
  const closeMutation = useCloseIncident();

  const [rootCause, setRootCause] = useState('');
  const [correctiveActions, setCorrectiveActions] = useState('');
  const [showResolveForm, setShowResolveForm] = useState(false);

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!incident) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-600">Incident report not found.</p>
        <Button asChild className="mt-4">
          <Link to="/safety/incidents">Back to Incidents</Link>
        </Button>
      </div>
    );
  }

  const handleInvestigate = () => {
    investigateMutation.mutate({ id: incident.id, data: {} });
  };

  const handleResolve = () => {
    resolveMutation.mutate({
      id: incident.id,
      data: { root_cause: rootCause, corrective_actions: correctiveActions },
    });
    setShowResolveForm(false);
  };

  const handleClose = () => {
    closeMutation.mutate(incident.id);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/safety/incidents')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{incident.title}</h1>
            <p className="text-gray-600">
              {incident.location} &middot; {formatDateOnly(incident.date)}
              {incident.time && ` at ${incident.time}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={severityColors[incident.severity]}>
            {incident.severity}
          </Badge>
          <Badge className={statusColors[incident.status]}>
            {statusLabels[incident.status]}
          </Badge>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Incident Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Reported By:</span>{' '}
              {incident.reported_by_username}
            </div>
            <div>
              <span className="text-gray-500">Category:</span>{' '}
              <span className="capitalize">{incident.category.replace('_', ' ')}</span>
            </div>
            <div>
              <span className="text-gray-500">Date:</span>{' '}
              {formatDateOnly(incident.date)}
            </div>
            <div>
              <span className="text-gray-500">Location:</span> {incident.location}
            </div>
            {incident.investigated_by_username && (
              <div>
                <span className="text-gray-500">Investigated By:</span>{' '}
                {incident.investigated_by_username}
              </div>
            )}
            {incident.injured_party && (
              <div>
                <span className="text-gray-500">Injured Party:</span>{' '}
                {incident.injured_party}
              </div>
            )}
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Description</p>
            <p className="text-sm whitespace-pre-wrap">{incident.description}</p>
          </div>
          {incident.witnesses && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Witnesses</p>
              <p className="text-sm whitespace-pre-wrap">{incident.witnesses}</p>
            </div>
          )}
          {incident.immediate_actions && (
            <div>
              <p className="text-sm text-gray-500 mb-1">Immediate Actions</p>
              <p className="text-sm whitespace-pre-wrap">{incident.immediate_actions}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {(incident.root_cause || incident.corrective_actions) && (
        <Card>
          <CardHeader>
            <CardTitle>Investigation & Resolution</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {incident.root_cause && (
              <div>
                <p className="text-sm text-gray-500 mb-1">Root Cause</p>
                <p className="text-sm whitespace-pre-wrap">{incident.root_cause}</p>
              </div>
            )}
            {incident.corrective_actions && (
              <div>
                <p className="text-sm text-gray-500 mb-1">Corrective Actions</p>
                <p className="text-sm whitespace-pre-wrap">{incident.corrective_actions}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {incident.photos && incident.photos.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Photos ({incident.photos.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {incident.photos.map((photo) => (
                <div key={photo.id} className="relative group">
                  <img
                    src={photo.file}
                    alt={photo.caption || 'Incident photo'}
                    className="w-full h-40 object-cover rounded-lg border"
                  />
                  {photo.caption && (
                    <p className="text-xs text-gray-500 mt-1">{photo.caption}</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {showResolveForm && (
        <Card>
          <CardHeader>
            <CardTitle>Resolve Incident</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="rootCause">Root Cause *</Label>
              <Textarea
                id="rootCause"
                value={rootCause}
                onChange={(e) => setRootCause(e.target.value)}
                rows={3}
                placeholder="What was the root cause of this incident..."
              />
            </div>
            <div>
              <Label htmlFor="corrective">Corrective Actions *</Label>
              <Textarea
                id="corrective"
                value={correctiveActions}
                onChange={(e) => setCorrectiveActions(e.target.value)}
                rows={3}
                placeholder="What corrective actions will prevent recurrence..."
              />
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleResolve}
                disabled={resolveMutation.isPending}
              >
                {resolveMutation.isPending ? 'Resolving...' : 'Resolve Incident'}
              </Button>
              <Button variant="outline" onClick={() => setShowResolveForm(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end gap-3">
        {canManageSafety && incident.status === 'reported' && (
          <Button
            onClick={handleInvestigate}
            disabled={investigateMutation.isPending}
            variant="outline"
          >
            {investigateMutation.isPending ? 'Starting...' : 'Begin Investigation'}
          </Button>
        )}
        {canManageSafety &&
          (incident.status === 'reported' || incident.status === 'under_investigation') && (
          <Button
            onClick={() => setShowResolveForm(true)}
            disabled={showResolveForm}
          >
            Resolve
          </Button>
        )}
        {canManageSafety && incident.status === 'resolved' && (
          <Button
            onClick={handleClose}
            disabled={closeMutation.isPending}
          >
            {closeMutation.isPending ? 'Closing...' : 'Close Incident'}
          </Button>
        )}
      </div>
    </div>
  );
}
